import {
    getQueryFromURLSearchParams,
    getURLSearchParamsFromQuery,
} from '@schibsted-nmp/search-frontend-clients';
import type { SearchKey } from '@schibsted-nmp/search-frontend-clients/quest';
import React from 'react';
import type { SearchParams } from '../state/AppStateTypes';
import { APP_URL_PARAMS } from '../state/UrlState';
import { useSearchParams } from './SearchParamsStore';

const ALL_SEARCHES = {};

type SearchQueryKeyVars = {
    searchKey?: SearchKey;
    searchParams?: SearchParams;
};

export function getSearchQueryKey(vars: SearchQueryKeyVars = ALL_SEARCHES) {
    return [{ scope: 'search', ...vars }] as const;
}

export function useSubmitSearch() {
    const { searchParams, setSearchParams } = useSearchParams();

    const submitSearch = React.useCallback(
        (
            updateParamsFn: (urlParams: URLSearchParams) => void,
            options: {
                keepPageParam?: boolean;
            } = {},
        ) => {
            const urlParams = getURLSearchParamsFromQuery(searchParams);

            updateParamsFn(urlParams);

            // Unless explictly specified, we always remove the page parameter
            // when changing the filters
            if (!options.keepPageParam) {
                urlParams.delete(APP_URL_PARAMS.page);
            }

            const updatedParams = getQueryFromURLSearchParams(urlParams);

            setSearchParams(updatedParams);
        },
        [searchParams, setSearchParams],
    );

    return { submitSearch };
}

export function useClearSearch() {
    const { searchParams, setSearchParams } = useSearchParams();

    const clearSearch = React.useCallback(() => {
        const clearParams: SearchParams = {};

        // Clear all params except sort
        const sortParam = searchParams[APP_URL_PARAMS.sort];
        if (sortParam) {
            clearParams[APP_URL_PARAMS.sort] = sortParam;
        }

        setSearchParams(clearParams);
    }, [searchParams, setSearchParams]);

    return { clearSearch };
}

export function deleteParamValue(
    urlParams: URLSearchParams,
    paramName: string,
    paramValue: string,
) {
    const currentValues = urlParams.getAll(paramName);
    const remainingValues = currentValues.filter(
        (value) => value !== paramValue,
    );
    urlParams.delete(paramName);
    remainingValues.forEach((value) => urlParams.append(paramName, value));
}
